import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users, UserPlus, Activity, Trash2, Edit, GitFork,
  Home, Info, Phone, ImageIcon, Package, CreditCard,
  FileText, MessageSquare, Stethoscope, Database as DatabaseIcon,
  UserCog, Pill, PhoneCall,
} from 'lucide-react';
import { LiveActivityView } from '../components/LiveActivityView';
import { AdminBannersView } from '../components/AdminBannersView';
import { AdminHomepageView } from '../components/AdminHomepageView';
import { AdminAboutView } from '../components/AdminAboutView';
import { AdminContactView } from '../components/AdminContactView';
import { AdminPsychologyContentView } from '../components/AdminPsychologyContentView';
import { AdminCallSettingsView } from '../components/AdminCallSettingsView';
import { AdminNotesView } from '../components/AdminNotesView';
import { AdminDatabaseView } from '../components/AdminDatabaseView';
import { AdminPatientsView } from '../components/AdminPatientsView';
import { AdminUserEditDialog } from '../components/AdminUserEditDialog';
import { AdminPackagesView } from '../components/AdminPackagesView';
import { AdminPaymentsView } from '../components/AdminPaymentsView';
import { AdminApplicationsView } from '../components/AdminApplicationsView';
import { AdminPhysioPackagesView } from '../components/AdminPhysioPackagesView';
import { AdminPsychologyPackagesView } from '../components/AdminPsychologyPackagesView';
import { AdminMessagesView } from '../components/AdminMessagesView';
import { DoctorOverflowDialog } from '../components/DoctorOverflowDialog';
import { ProfilePictureUploader } from '../components/ProfilePictureUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [doctors, setDoctors] = useState([]);
  // Answered-call counts per provider id — admin-only view of a private metric.
  const [callStats, setCallStats] = useState({});
  const [pharmacists, setPharmacists] = useState([]);
  const [patients, setPatients] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [showDoctorForm, setShowDoctorForm] = useState(false);
  const [showPharmacistForm, setShowPharmacistForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [managedUserId, setManagedUserId] = useState(null);

  const [doctorForm, setDoctorForm] = useState({
    full_name: '',
    email: '',
    password: '',
    phone_number: '',
    specialty: '',
    biography: '',
    category: 'doctors',
    profile_photo: null
  });

  const [pharmacistForm, setPharmacistForm] = useState({
    full_name: '',
    email: '',
    password: '',
    phone_number: '',
    pharmacy_name: '',
    profile_photo: null
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [doctorsRes, pharmacistsRes, patientsRes, logsRes, callStatsRes] = await Promise.all([
        axios.get(`${API}/admin/users?role=doctor`, { withCredentials: true }),
        axios.get(`${API}/admin/users?role=pharmacist`, { withCredentials: true }),
        axios.get(`${API}/admin/users?role=patient`, { withCredentials: true }),
        axios.get(`${API}/admin/activity-logs`, { withCredentials: true }),
        axios.get(`${API}/admin/providers/call-stats`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);

      setDoctors(doctorsRes.data);
      setPharmacists(pharmacistsRes.data);
      setPatients(patientsRes.data);
      setActivityLogs(logsRes.data);
      setCallStats(callStatsRes.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCreateDoctor = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await axios.post(`${API}/admin/doctors`, doctorForm, { withCredentials: true });
      setMessage(t('تم إنشاء حساب الطبيب بنجاح', 'Doctor account created successfully'));
      setShowDoctorForm(false);
      setDoctorForm({
        full_name: '',
        email: '',
        password: '',
        phone_number: '',
        specialty: '',
        biography: '',
        category: 'doctors',
        profile_photo: null
      });
      fetchAllData();
    } catch (error) {
      setMessage(error.response?.data?.detail || t('حدث خطأ', 'Error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePharmacist = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await axios.post(`${API}/admin/pharmacists`, pharmacistForm, { withCredentials: true });
      setMessage(t('تم إنشاء حساب الصيدلي بنجاح', 'Pharmacist account created successfully'));
      setShowPharmacistForm(false);
      setPharmacistForm({
        full_name: '',
        email: '',
        password: '',
        phone_number: '',
        pharmacy_name: '',
        profile_photo: null
      });
      fetchAllData();
    } catch (error) {
      setMessage(error.response?.data?.detail || t('حدث خطأ', 'Error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('هل أنت متأكد من حذف هذا المستخدم؟', 'Are you sure you want to delete this user?'))) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      setMessage(t('تم حذف المستخدم بنجاح', 'User deleted successfully'));
      fetchAllData();
    } catch (error) {
      setMessage(error.response?.data?.detail || t('حدث خطأ', 'Error occurred'));
    }
  };

  const handleToggleAvailability = async (doctorId, currentValue) => {
    try {
      await axios.put(`${API}/admin/doctors/${doctorId}/availability`,
        { is_available: !currentValue }, { withCredentials: true });
      fetchAllData();
    } catch (error) {
      setMessage(error.response?.data?.detail || t('حدث خطأ', 'Error occurred'));
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b from-slate-50 to-white ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-7xl">
        {/* Compact header — smaller on mobile */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-[#0d47a1]" data-testid="admin-dashboard-title">
            {t('لوحة تحكم المسؤول', 'Admin Dashboard')}
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            {t('مرحباً', 'Welcome')}{' '}
            <span className="font-semibold text-slate-800">{user?.full_name}</span>
          </p>
        </div>

        {message && (
          <Alert className="mb-4">
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {/* Stats — 3 cards, stack on mobile, colored icons */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Card className="p-3 sm:p-5 border-blue-100">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-gray-500 text-[11px] sm:text-sm truncate">{t('الأطباء', 'Doctors')}</p>
                <p className="text-xl sm:text-3xl font-extrabold text-[#1e88e5]">{doctors.length}</p>
              </div>
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-blue-100 text-[#1e88e5] flex items-center justify-center flex-shrink-0">
                <Stethoscope size={20} />
              </div>
            </div>
          </Card>

          <Card className="p-3 sm:p-5 border-emerald-100">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-gray-500 text-[11px] sm:text-sm truncate">{t('الصيادلة', 'Pharmacists')}</p>
                <p className="text-xl sm:text-3xl font-extrabold text-emerald-600">{pharmacists.length}</p>
              </div>
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <Pill size={20} />
              </div>
            </div>
          </Card>

          <Card className="p-3 sm:p-5 border-amber-100">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-gray-500 text-[11px] sm:text-sm truncate">{t('المرضى', 'Patients')}</p>
                <p className="text-xl sm:text-3xl font-extrabold text-amber-600">{patients.length}</p>
              </div>
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
                <Users size={20} />
              </div>
            </div>
          </Card>
        </div>

        <Tabs defaultValue="doctors" className="w-full">
          {/* Scrollable / wrapping tab bar — mobile friendly.
              - On mobile: horizontal scroll with pill tabs (no giant grid).
              - On larger screens: pills wrap onto multiple rows. */}
          <TabsList
            className="flex w-full h-auto flex-nowrap md:flex-wrap gap-1.5 p-2 mb-4 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-x-auto scrollbar-none"
            data-testid="admin-tabs-list"
          >
            <TabsTrigger
              value="doctors"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
              data-testid="admin-tab-doctors"
            >
              <Stethoscope size={14} />
              <span>{t('الأطباء', 'Doctors')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="pharmacists"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
              data-testid="admin-tab-pharmacists"
            >
              <Pill size={14} />
              <span>{t('الصيادلة', 'Pharmacists')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="patients"
              data-testid="admin-tab-patients"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <UserCog size={14} />
              <span>{t('المرضى', 'Patients')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="homepage"
              data-testid="admin-tab-homepage"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <Home size={14} />
              <span>{t('الرئيسية', 'Homepage')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="about"
              data-testid="admin-tab-about"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <Info size={14} />
              <span>{t('من نحن', 'About')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="contact"
              data-testid="admin-tab-contact"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <Phone size={14} />
              <span>{t('تواصل معنا', 'Contact')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="banners"
              data-testid="admin-tab-banners"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <ImageIcon size={14} />
              <span>{t('الشرائح', 'Banners')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="psychology-banner"
              data-testid="admin-tab-psychology-banner"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <ImageIcon size={14} />
              <span>{t('بانر علم النفس', 'Psy Banner')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="packages"
              data-testid="admin-tab-packages"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <Package size={14} />
              <span>{t('الباقات', 'Packages')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="payments"
              data-testid="admin-tab-payments"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <CreditCard size={14} />
              <span>{t('المدفوعات', 'Payments')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="applications"
              data-testid="admin-tab-applications"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <FileText size={14} />
              <span>{t('الطلبات', 'Applications')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="messages"
              data-testid="admin-tab-messages"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <MessageSquare size={14} />
              <span>{t('الرسائل', 'Messages')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="notes"
              data-testid="admin-tab-notes"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <FileText size={14} />
              <span>{t('الملاحظات', 'Notes')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="database"
              data-testid="admin-tab-database"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <DatabaseIcon size={14} />
              <span>{t('البيانات', 'Database')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="logs"
              data-testid="admin-tab-activity"
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap data-[state=active]:bg-[#1e88e5] data-[state=active]:text-white data-[state=active]:shadow"
            >
              <Activity size={14} />
              <span>{t('النشاط', 'Activity')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="doctors" className="space-y-4">
            <Dialog open={showDoctorForm} onOpenChange={setShowDoctorForm}>
              <DialogTrigger asChild>
                <Button className="bg-[#1e88e5] hover:bg-[#1976d2]" data-testid="add-doctor-button">
                  <UserPlus className={language === 'ar' ? 'ml-2' : 'mr-2'} size={20} />
                  {t('إضافة طبيب', 'Add Doctor')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('إضافة طبيب جديد', 'Add New Doctor')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateDoctor} className="space-y-4">
                  <ProfilePictureUploader
                    value={doctorForm.profile_photo}
                    onChange={(url) => setDoctorForm({ ...doctorForm, profile_photo: url })}
                    draftOnly
                    testId="doctor-form-pic"
                    size={84}
                  />
                  <div>
                    <Label>{t('الاسم الكامل', 'Full Name')}</Label>
                    <Input
                      value={doctorForm.full_name}
                      onChange={(e) => setDoctorForm({ ...doctorForm, full_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('البريد الإلكتروني', 'Email')}</Label>
                    <Input
                      type="email"
                      value={doctorForm.email}
                      onChange={(e) => setDoctorForm({ ...doctorForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('كلمة المرور', 'Password')}</Label>
                    <Input
                      type="password"
                      value={doctorForm.password}
                      onChange={(e) => setDoctorForm({ ...doctorForm, password: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('رقم الهاتف', 'Phone Number')}</Label>
                    <Input
                      value={doctorForm.phone_number}
                      onChange={(e) => setDoctorForm({ ...doctorForm, phone_number: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('التخصص', 'Specialty')}</Label>
                    <Input
                      value={doctorForm.specialty}
                      onChange={(e) => setDoctorForm({ ...doctorForm, specialty: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('الفئة', 'Category')}</Label>
                    <Select
                      value={doctorForm.category}
                      onValueChange={(value) => setDoctorForm({ ...doctorForm, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="doctors">{t('أطباء', 'Doctors')}</SelectItem>
                        <SelectItem value="physiotherapy">{t('علاج طبيعي', 'Physiotherapy')}</SelectItem>
                        <SelectItem value="psychology">{t('علم النفس', 'Psychology')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('السيرة الذاتية', 'Biography')}</Label>
                    <Textarea
                      value={doctorForm.biography}
                      onChange={(e) => setDoctorForm({ ...doctorForm, biography: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#1e88e5] hover:bg-[#1976d2]">
                    {loading ? t('جاري الإنشاء...', 'Creating...') : t('إنشاء حساب', 'Create Account')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <div className="grid gap-4">
              {doctors.map((doctor) => (
                <Card key={doctor._id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{doctor.full_name}</h3>
                      <p className="text-gray-600">{doctor.email}</p>
                      <p className="text-sm text-gray-500">{doctor.specialty}</p>
                      <p className="text-sm text-gray-500">{doctor.phone_number}</p>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          doctor.is_available !== false ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {doctor.is_available !== false ? t('متاح', 'Available') : t('غير متاح', 'Unavailable')}
                        </span>
                        {doctor.is_online && (
                          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                            {t('متصل', 'Online')}
                          </span>
                        )}
                        <span
                          className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-700 inline-flex items-center gap-1"
                          data-testid={`call-count-chip-${doctor._id}`}
                          title={t('عدد المكالمات المُجابة', 'Answered calls')}
                        >
                          <PhoneCall size={11} />
                          {t(
                            `${callStats[doctor._id]?.total || 0} مكالمة`,
                            `${callStats[doctor._id]?.total || 0} calls`
                          )}
                          {callStats[doctor._id]?.this_month
                            ? ` · ${callStats[doctor._id].this_month} ${t('هذا الشهر', 'this month')}`
                            : ''}
                        </span>
                        {doctor.overflow_doctor_id && (
                          <span
                            className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700 inline-flex items-center gap-1"
                            data-testid={`overflow-chip-${doctor._id}`}
                            title={t('قائمة الانتظار → طبيب آخر', 'Overflow chain set', 'Chaîne configurée')}
                          >
                            <GitFork size={11} />
                            {(() => {
                              const nxt = doctors.find((d) => d._id === doctor.overflow_doctor_id);
                              return nxt ? `→ ${nxt.full_name}` : t('سلسلة', 'Overflow set', 'Chaîne');
                            })()}
                          </span>
                        )}
                        {doctor.category === 'psychology' && (
                          <span className="px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-700" data-testid={`psy-cat-chip-${doctor._id}`}>
                            {(() => {
                              const labels = {
                                adult: t('بالغين', 'Adult'),
                                child_teen: t('أطفال ومراهقون', 'Child & Teen'),
                                family_couples: t('أسرة وأزواج', 'Family & Couples'),
                              };
                              return labels[doctor.psychology_category] || t('لم يُحدَّد', 'No sub-category');
                            })()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      {doctor.category === 'psychology' && (
                        <Select
                          value={doctor.psychology_category || 'none'}
                          onValueChange={async (v) => {
                            try {
                              await axios.put(
                                `${API}/admin/doctors/${doctor._id}/psychology-category`,
                                { psychology_category: v === 'none' ? null : v },
                                { withCredentials: true },
                              );
                              fetchAllData();
                            } catch { /* toast handled elsewhere */ }
                          }}
                        >
                          <SelectTrigger className="w-[180px]" data-testid={`psy-cat-select-${doctor._id}`}>
                            <SelectValue placeholder={t('التخصص الفرعي', 'Sub-category')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('لم يُحدَّد', 'No sub-category')}</SelectItem>
                            <SelectItem value="adult">{t('صحة نفسية للبالغين', 'Adult Mental Health')}</SelectItem>
                            <SelectItem value="child_teen">{t('أطفال ومراهقون', 'Child & Adolescent')}</SelectItem>
                            <SelectItem value="family_couples">{t('أسرة وأزواج', 'Family & Couples')}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <DoctorOverflowDialog
                        doctor={doctor}
                        doctors={doctors}
                        onChanged={fetchAllData}
                        trigger={
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-purple-200 text-purple-700 hover:bg-purple-50"
                            data-testid={`overflow-btn-${doctor._id}`}
                          >
                            <GitFork size={14} className="me-1" />
                            {t('قائمة الانتظار', 'Overflow')}
                          </Button>
                        }
                      />
                      <Button
                        size="sm"
                        variant={doctor.is_available !== false ? 'outline' : 'default'}
                        onClick={() => handleToggleAvailability(doctor._id, doctor.is_available !== false)}
                        className={doctor.is_available !== false ? '' : 'bg-green-600 hover:bg-green-700'}
                        data-testid={`toggle-availability-${doctor._id}`}
                      >
                        {doctor.is_available !== false ? t('تعطيل', 'Disable') : t('تفعيل', 'Enable')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setManagedUserId(doctor._id)}
                        className="border-blue-200 text-[#1565c0] hover:bg-blue-50"
                        data-testid={`manage-doctor-${doctor._id}`}
                      >
                        <Edit size={14} className="me-1" />
                        {t('إدارة', 'Manage')}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteUser(doctor._id)}
                        data-testid={`delete-doctor-${doctor._id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="patients" className="space-y-4">
            <AdminPatientsView />
          </TabsContent>

          <TabsContent value="pharmacists" className="space-y-4">
            <Dialog open={showPharmacistForm} onOpenChange={setShowPharmacistForm}>
              <DialogTrigger asChild>
                <Button className="bg-[#1e88e5] hover:bg-[#1976d2]" data-testid="add-pharmacist-button">
                  <UserPlus className={language === 'ar' ? 'ml-2' : 'mr-2'} size={20} />
                  {t('إضافة صيدلي', 'Add Pharmacist')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t('إضافة صيدلي جديد', 'Add New Pharmacist')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreatePharmacist} className="space-y-4">
                  <ProfilePictureUploader
                    value={pharmacistForm.profile_photo}
                    onChange={(url) => setPharmacistForm({ ...pharmacistForm, profile_photo: url })}
                    draftOnly
                    testId="pharmacist-form-pic"
                    size={84}
                  />
                  <div>
                    <Label>{t('الاسم الكامل', 'Full Name')}</Label>
                    <Input
                      value={pharmacistForm.full_name}
                      onChange={(e) => setPharmacistForm({ ...pharmacistForm, full_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('البريد الإلكتروني', 'Email')}</Label>
                    <Input
                      type="email"
                      value={pharmacistForm.email}
                      onChange={(e) => setPharmacistForm({ ...pharmacistForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('كلمة المرور', 'Password')}</Label>
                    <Input
                      type="password"
                      value={pharmacistForm.password}
                      onChange={(e) => setPharmacistForm({ ...pharmacistForm, password: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('رقم الهاتف', 'Phone Number')}</Label>
                    <Input
                      value={pharmacistForm.phone_number}
                      onChange={(e) => setPharmacistForm({ ...pharmacistForm, phone_number: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('اسم الصيدلية', 'Pharmacy Name')}</Label>
                    <Input
                      value={pharmacistForm.pharmacy_name}
                      onChange={(e) => setPharmacistForm({ ...pharmacistForm, pharmacy_name: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#1e88e5] hover:bg-[#1976d2]">
                    {loading ? t('جاري الإنشاء...', 'Creating...') : t('إنشاء حساب', 'Create Account')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <div className="grid gap-4">
              {pharmacists.map((pharmacist) => (
                <Card key={pharmacist._id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-lg">{pharmacist.full_name}</h3>
                      <p className="text-gray-600">{pharmacist.email}</p>
                      <p className="text-sm text-gray-500">{pharmacist.pharmacy_name}</p>
                      <p className="text-sm text-gray-500">{pharmacist.phone_number}</p>
                      <span
                        className="mt-2 px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-700 inline-flex items-center gap-1"
                        data-testid={`call-count-chip-${pharmacist._id}`}
                        title={t('عدد المكالمات المُجابة', 'Answered calls')}
                      >
                        <PhoneCall size={11} />
                        {t(
                          `${callStats[pharmacist._id]?.total || 0} مكالمة`,
                          `${callStats[pharmacist._id]?.total || 0} calls`
                        )}
                      </span>
                    </div>
                    <div className="flex gap-2 items-start">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setManagedUserId(pharmacist._id)}
                        className="border-blue-200 text-[#1565c0] hover:bg-blue-50"
                        data-testid={`manage-pharmacist-${pharmacist._id}`}
                      >
                        <Edit size={14} className="me-1" />
                        {t('إدارة', 'Manage')}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteUser(pharmacist._id)}
                        data-testid={`delete-pharmacist-${pharmacist._id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="banners" className="space-y-4">
            <AdminBannersView />
          </TabsContent>

          <TabsContent value="homepage" className="space-y-4">
            <AdminHomepageView />
          </TabsContent>

          <TabsContent value="about" className="space-y-4">
            <AdminAboutView />
          </TabsContent>

          <TabsContent value="contact" className="space-y-4">
            <AdminContactView />
          </TabsContent>

          <TabsContent value="psychology-banner" className="space-y-4">
            <AdminPsychologyContentView />
          </TabsContent>

          <TabsContent value="packages" className="space-y-6">
            <AdminCallSettingsView />
            <hr className="border-gray-200 my-4" />
            <AdminPackagesView />
            <hr className="border-gray-200 my-4" />
            <AdminPhysioPackagesView />
            <hr className="border-gray-200 my-4" />
            <AdminPsychologyPackagesView />
          </TabsContent>

          <TabsContent value="payments" className="space-y-4">
            <AdminPaymentsView />
          </TabsContent>

          <TabsContent value="applications" className="space-y-4">
            <AdminApplicationsView />
          </TabsContent>

          <TabsContent value="messages" className="space-y-4">
            <AdminMessagesView />
          </TabsContent>

          <TabsContent value="notes" className="space-y-4">
            <AdminNotesView />
          </TabsContent>

          <TabsContent value="database" className="space-y-4">
            <AdminDatabaseView />
          </TabsContent>

          <TabsContent value="logs">
            <LiveActivityView />
          </TabsContent>
        </Tabs>
      </div>

      <AdminUserEditDialog
        open={!!managedUserId}
        onOpenChange={(v) => !v && setManagedUserId(null)}
        userId={managedUserId}
        onSaved={fetchAllData}
      />
    </div>
  );
};
